import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FormUpload, FormUploadService } from './form-upload.service';
import { FormUploadFieldComponent } from './form-upload-field.component';
import { I18nService } from '../../core/i18n.service';
import { TarotSpreadComponent } from '../../shared/tarot-spread.component';

const FORM_COPY = {
  tr:{requiredLabel:'zorunlu',choose:'Seç',empty:'Bu form için gösterilecek alan bulunmuyor.',uploadFailed:'Dosya yüklenemedi.',field:'Alan',required:'zorunludur.',invalid:'geçerli değil.',short:'için girilen değer çok kısa.',long:'için girilen değer çok uzun.',check:'Alanı kontrol et.'},
  en:{requiredLabel:'required',choose:'Choose',empty:'There are no fields to display for this form.',uploadFailed:'The file could not be uploaded.',field:'Field',required:'is required.',invalid:'is not valid.',short:'is too short.',long:'is too long.',check:'Check this field.'},
  de:{requiredLabel:'Pflichtfeld',choose:'Auswählen',empty:'Für dieses Formular sind keine Felder vorhanden.',uploadFailed:'Die Datei konnte nicht hochgeladen werden.',field:'Feld',required:'ist erforderlich.',invalid:'ist ungültig.',short:'ist zu kurz.',long:'ist zu lang.',check:'Bitte Feld prüfen.'},
  es:{requiredLabel:'obligatorio',choose:'Elegir',empty:'No hay campos para mostrar en este formulario.',uploadFailed:'No se pudo subir el archivo.',field:'Campo',required:'es obligatorio.',invalid:'no es válido.',short:'es demasiado corto.',long:'es demasiado largo.',check:'Revisa este campo.'},
  fr:{requiredLabel:'obligatoire',choose:'Choisir',empty:'Aucun champ à afficher pour ce formulaire.',uploadFailed:'Impossible de téléverser le fichier.',field:'Champ',required:'est obligatoire.',invalid:'n’est pas valide.',short:'est trop court.',long:'est trop long.',check:'Vérifiez ce champ.'}
} as const;

export interface DynamicFormField {
  id?: number;
  formKey?: string;
  type?: string;
  options?: unknown;
  required?: boolean | number;
  minlenght?: number | string | null;
  maxlenght?: number | string | null;
  minlength?: number | string | null;
  maxlength?: number | string | null;
  minLength?: number | string | null;
  maxLength?: number | string | null;
  multiple?: boolean | number;
  minFiles?: number | string | null;
  maxFiles?: number | string | null;
  title?: string;
  description?: string | null;
}

@Component({
  selector: 'app-dynamic-form-fields',
  imports: [FormsModule, FormUploadFieldComponent, TarotSpreadComponent],
  template: `
    <div class="form-fields">
      @for (field of fields; track fieldKey(field)) {
        <label [class.file-field]="isFileField(field) || isTarotField(field)">
          <span>{{ fieldLabel(field) }} @if(required(field)){<b class="required-mark" [attr.aria-label]="copy().requiredLabel">*</b>}</span>
          @if (field.description) { <small>{{ field.description }}</small> }
          @if (errors[fieldKey(field)]) {
            <em class="field-error">{{ errorMessage(field, errors[fieldKey(field)]) }}</em>
          }

          @if (isTarotField(field)) {
            <app-tarot-spread [value]="model[fieldKey(field)]" [allowedSpreads]="tarotSpreadKeys(field)" (valueChange)="setValue(field,$event)" />
          } @else if (isRadio(field) && fieldOptions(field).length) {
            <div class="radio-pills">
              @for (option of fieldOptions(field); track option.value) {
                <button type="button" [class.selected]="model[fieldKey(field)] === option.value" [attr.aria-pressed]="model[fieldKey(field)] === option.value" (click)="setValue(field, option.value)">{{ option.label }}</button>
              }
            </div>
          } @else if (isCheckbox(field) && fieldOptions(field).length) {
            <div class="checkbox-list">
              @for (option of fieldOptions(field); track option.value) {
                <label><input type="checkbox" [checked]="checkboxValue(field, option.value)" (change)="toggleCheckbox(field, option.value, $event)" /> {{ option.label }}</label>
              }
            </div>
          } @else if (fieldOptions(field).length) {
            <select [ngModel]="model[fieldKey(field)]" (ngModelChange)="setValue(field,$event)" [name]="fieldKey(field)" [attr.name]="fieldKey(field)" [required]="required(field)">
              <option value="">{{copy().choose}}</option>
              @for (option of fieldOptions(field); track option.value) {
                <option [value]="option.value">{{ option.label }}</option>
              }
            </select>
          } @else if (isLongText(field)) {
            <textarea [ngModel]="model[fieldKey(field)]" (ngModelChange)="setValue(field,$event)" [name]="fieldKey(field)" [attr.name]="fieldKey(field)" rows="4" [required]="required(field)" [minlength]="fieldMin(field)" [maxlength]="fieldMax(field)"></textarea>
          } @else if (isFileField(field)) {
            <app-form-upload-field
              [fieldKey]="fieldKey(field)"
              [fieldType]="fieldType(field)"
              [minFiles]="fileMinimum(field)"
              [maxFiles]="fileLimit(field)"
              [value]="uploads(field)"
              [uploading]="uploadingKey === fieldKey(field)"
              (uploadState)="uploadingKey = $event ? fieldKey(field) : ''"
              (valueChange)="setValue(field,$event); uploadComplete.emit({ key: fieldKey(field), uploads: $event })"
              (uploadError)="uploadError.emit({ key: fieldKey(field), message: $event })"
            />
          } @else {
            <input
              [ngModel]="model[fieldKey(field)]"
              (ngModelChange)="setValue(field,$event)"
              [name]="fieldKey(field)"
              [attr.name]="fieldKey(field)"
              [type]="inputType(field)"
              [required]="required(field)"
              [minlength]="fieldMin(field)"
              [maxlength]="fieldMax(field)"
              [attr.pattern]="fieldKey(field) === 'shopName' ? '[A-Za-z0-9._-]+' : null"
              (blur)="fieldBlur.emit(field)"
            />
          }
          @if (hints[fieldKey(field)]) {
            <em class="field-hint">{{ hints[fieldKey(field)] }}</em>
          }
        </label>
      } @empty {
        <div class="empty">{{copy().empty}}</div>
      }
    </div>
  `,
  styles: [`
    .form-fields { display:grid; gap:16px; grid-template-columns:repeat(2,minmax(0,1fr)); }
    label { display:grid; gap:8px; }
    label > span { color:var(--ink); font-size:13px; font-weight:900; }
    label > small, label > em { color:var(--muted); font-size:12px; line-height:1.45; }
    input, select, textarea {
      box-sizing:border-box;
      background:color-mix(in srgb,var(--paper) 82%,var(--surface));
      border:1px solid var(--border); border-radius:16px; color:var(--ink);
      font:inherit; outline:0; padding:13px 14px; width:100%;
    }
    input:focus, select:focus, textarea:focus { border-color:color-mix(in srgb,var(--accent) 55%,var(--border)); }
    .file-field { grid-column:1/-1; }
    .field-error {
      background:rgb(220 59 99 / 12%); border:1px solid rgb(220 59 99 / 24%);
      border-radius:12px; color:#b42b55; font-style:normal; font-weight:850; padding:9px 11px;
    }
    .field-hint { font-style:normal; font-weight:800; }
    .required-mark { color:#c40000; font-size:17px; line-height:0; }
    .upload-state { align-items:center; color:var(--accent); display:flex; font-style:normal; gap:8px; }
    .upload-state i { animation:pulse 900ms ease-in-out infinite; background:currentColor; border-radius:50%; height:9px; width:9px; }
    .uploaded-files { display:grid; gap:8px; }
    .uploaded-files span {
      align-items:center; background:color-mix(in srgb,var(--paper) 72%,transparent);
      border:1px solid var(--border); border-radius:16px; display:grid; gap:2px 10px;
      grid-template-columns:auto 1fr auto; padding:9px; position:relative;
    }
    .uploaded-files img { border-radius:10px; grid-row:span 2; height:46px; object-fit:cover; width:46px; }
    .uploaded-files small { color:#1b7a53; }
    .remove-upload { align-self:center; background:transparent; border:1px solid var(--border); border-radius:999px; color:var(--ink); cursor:pointer; font:inherit; font-weight:900; grid-column:3; grid-row:span 2; height:30px; line-height:1; padding:0; width:30px; }
    .empty { color:var(--muted); grid-column:1/-1; padding:20px 0; } .radio-pills,.checkbox-list{display:flex;flex-wrap:wrap;gap:10px}.radio-pills button{background:transparent;border:1px solid var(--border);border-radius:999px;color:var(--ink);cursor:pointer;font:inherit;padding:10px 14px}.radio-pills button.selected{background:var(--accent)!important;border-color:var(--accent)!important;color:var(--accent-contrast,#fff)!important;font-weight:900}.radio-pills button.selected::before{content:'✓';margin-right:7px}.checkbox-list label{align-items:center;display:flex;gap:8px;margin:0}.checkbox-list input{width:auto}
    @keyframes pulse { 50% { opacity:.35; } }
    :host-context(html[data-design='falderyasi']) .form-fields { grid-template-columns:1fr; }
    @media(max-width:580px){ .form-fields { grid-template-columns:1fr; } }
  `]
})
export class DynamicFormFieldsComponent {
  private readonly uploader = inject(FormUploadService);
  private readonly i18n = inject(I18nService);
  protected copy(){return FORM_COPY[this.i18n.lang()]||FORM_COPY.en;}
  @Input() fields: DynamicFormField[] = [];
  @Input() model: Record<string, any> = {};
  @Input() errors: Record<string, string> = {};
  @Input() hints: Record<string, string> = {};
  @Output() uploading = new EventEmitter<string>();
  @Output() uploadError = new EventEmitter<{ key: string; message: string }>();
  @Output() uploadComplete = new EventEmitter<{ key: string; uploads: FormUpload[] }>();
  @Output() fieldBlur = new EventEmitter<DynamicFormField>();
  @Output() modelChange = new EventEmitter<Record<string, any>>();
  protected uploadingKey = '';

  protected async selectFiles(field: DynamicFormField, event: Event): Promise<void> {
    const key = this.fieldKey(field);
    const files = Array.from((event.target as HTMLInputElement).files ?? []);
    if (!files.length) {
      this.model[key] = [];
      return;
    }
    this.uploadingKey = key;
    this.uploading.emit(key);
    try {
      const uploads: FormUpload[] = this.isMultiFile(field) ? [...this.uploads(field)] : [];
      for (const file of files) uploads.push(await this.uploader.upload(key, file));
      this.model[key] = uploads;
      this.uploadComplete.emit({ key, uploads });
    } catch (error) {
      this.model[key] = [];
      this.uploadError.emit({ key, message: error instanceof Error ? error.message : this.copy().uploadFailed });
    } finally {
      this.uploadingKey = '';
      this.uploading.emit('');
    }
  }

  protected fieldKey(field: DynamicFormField): string { return String(field.formKey || `field_${field.id || 'x'}`); }
  protected setValue(field:DynamicFormField,value:any):void { const key=this.fieldKey(field);this.model[key]=value;if(this.errors[key])delete this.errors[key];this.modelChange.emit(this.model); }
  protected fieldType(field: DynamicFormField): string { return String(field.type || 'image'); }
  protected fieldLabel(field: DynamicFormField): string { return String(field.title || field.formKey || this.copy().field); }
  protected required(field: DynamicFormField): boolean { return field.required === true || Number(field.required || 0) === 1; }
  protected isRadio(field: DynamicFormField): boolean { return String(field.type || '').toLowerCase() === 'radio'; }
  protected isCheckbox(field: DynamicFormField): boolean { return String(field.type || '').toLowerCase() === 'checkbox'; }
  protected fieldMin(field: DynamicFormField): number | null { const value=Number(field.minLength ?? field.minlenght ?? field.minlength ?? 0); return value>0 ? value : null; }
  protected fieldMax(field: DynamicFormField): number | null { const value=Number(field.maxLength ?? field.maxlenght ?? field.maxlength ?? 0); return value>0 ? value : null; }
  protected isLongText(field: DynamicFormField): boolean { return ['textarea','longtext'].includes(String(field.type || '').toLowerCase()); }
  protected isFileField(field: DynamicFormField): boolean { return ['image','picture','file','media','gallery','video'].includes(String(field.type || '').toLowerCase()); }
  protected isTarotField(field: DynamicFormField): boolean { return String(field.type || '').toLowerCase() === 'tarot'; }
  protected tarotSpreadKeys(field:DynamicFormField):string[]{return this.fieldOptions(field).map(option=>option.value);}
  protected isMultiFile(field: DynamicFormField): boolean {
    const type = String(field.type || '').toLowerCase();
    const maxFiles = Number(field.maxFiles ?? field.maxLength ?? field.maxlenght ?? field.maxlength ?? 0);
    return ['gallery', 'media'].includes(type) || field.multiple === true || Number(field.multiple || 0) === 1 || maxFiles > 1;
  }
  protected fileMinimum(field:DynamicFormField):number { const value=Number(field.minFiles ?? field.minLength ?? field.minlenght ?? field.minlength ?? 0); return Math.min(Math.max(value||0,0),12); }
  protected fileLimit(field: DynamicFormField): number { const value=Number(field.maxFiles ?? field.maxLength ?? field.maxlenght ?? field.maxlength ?? 0); return Math.min(Math.max(value || (this.isMultiFile(field) ? 4 : 1),1),12); }
  protected inputType(field: DynamicFormField): string {
    const type = String(field.type || 'text').toLowerCase();
    if (['email','mail'].includes(type)) return 'email';
    if (['phone','tel'].includes(type)) return 'tel';
    if (['number','price','decimal'].includes(type)) return 'number';
    return ['date','datetime-local','time','password'].includes(type) ? type : 'text';
  }
  protected uploads(field: DynamicFormField): FormUpload[] {
    const value = this.model[this.fieldKey(field)];
    return Array.isArray(value) ? value.filter((item) => Number(item?.uploadID || 0) > 0) : [];
  }
  protected removeUpload(field: DynamicFormField, uploadID: number): void {
    const key = this.fieldKey(field);
    const removed = this.uploads(field).find((upload) => Number(upload.uploadID) === Number(uploadID));
    if (removed?.tempFile && /^blob:/i.test(String(removed.tempFile))) {
      URL.revokeObjectURL(String(removed.tempFile));
    }
    const next = this.uploads(field).filter((upload) => Number(upload.uploadID) !== Number(uploadID));
    this.model[key] = next;
  }
  protected checkboxValue(field: DynamicFormField, value: string): boolean { const current=this.model[this.fieldKey(field)]; return !!(current && typeof current==='object' && !Array.isArray(current) && current[value]); }
  protected toggleCheckbox(field: DynamicFormField, value: string, event: Event): void { const current=this.model[this.fieldKey(field)]; const next=current && typeof current==='object' && !Array.isArray(current) ? {...current} : {}; next[value]=!!(event.target as HTMLInputElement).checked; this.model[this.fieldKey(field)]=next;this.modelChange.emit(this.model); }
  protected fieldOptions(field: DynamicFormField): Array<{ value: string; label: string }> {
    const options=Array.isArray(field.options) ? field.options.map((item:any)=>typeof item==='object'?{value:String(item.value??item.id??''),label:String(item.label??item.title??item.name??item.value??'')}:{value:String(item),label:String(item)}) : field.options&&typeof field.options==='object'?Object.entries(field.options).map(([value,label])=>({value,label:String(label)})):[];
    const labels:Record<string,Record<string,string>>={
      man:{tr:'Erkek',en:'Man',de:'Mann',es:'Hombre',fr:'Homme'},erkek:{tr:'Erkek',en:'Man',de:'Mann',es:'Hombre',fr:'Homme'},
      women:{tr:'Kadın',en:'Woman',de:'Frau',es:'Mujer',fr:'Femme'},kadin:{tr:'Kadın',en:'Woman',de:'Frau',es:'Mujer',fr:'Femme'},
      null:{tr:'Belirtmek istemiyorum',en:'Prefer not to say',de:'Keine Angabe',es:'Prefiero no decirlo',fr:'Je préfère ne pas le préciser'},belirtmekistemiyorum:{tr:'Belirtmek istemiyorum',en:'Prefer not to say',de:'Keine Angabe',es:'Prefiero no decirlo',fr:'Je préfère ne pas le préciser'},
      bekar:{tr:'Bekar',en:'Single',de:'Ledig',es:'Soltero/a',fr:'Célibataire'},iliskisivar:{tr:'İlişkisi var',en:'In a relationship',de:'In einer Beziehung',es:'En una relación',fr:'En couple'},evli:{tr:'Evli',en:'Married',de:'Verheiratet',es:'Casado/a',fr:'Marié(e)'}
    };
    return options.map(option=>({...option,label:labels[option.value.toLocaleLowerCase('tr-TR')]?.[this.i18n.lang()]||option.label}));
  }
  protected errorMessage(field: DynamicFormField, error: string): string {
    const label = this.fieldLabel(field);
    const value = String(error || '').toLowerCase();
    if (value === 'required') return `${label} ${this.copy().required}`;
    if (value === 'notvalid') return `${label} ${this.copy().invalid}`;
    if (['minlenght','minlength'].includes(value)) return `${label} ${this.copy().short}`;
    if (['maxlenght','maxlength'].includes(value)) return `${label} ${this.copy().long}`;
    return `${label}: ${error || this.copy().check}`;
  }
}
